import { Component as VueComponent, h, onScopeDispose, Ref, VNode, watch, watchEffect, WatchStopHandle } from "vue";
import { SkyVueTypes } from "../..";
import { SkywardException } from "../../../../skyward.base.error";
import { Dictionary } from "../../../../types";
import Cma from "../../../core/cma";
import { ErrorStates, State } from "../store/types";
import { Component as SkyVueComponent, isSkyVueComponent } from "../store/features/components";
import { TileErrorContent } from "../../dashboardItem/components/error/errorBody";
import { DashboardItemFeatures } from "../../dashboardItem/store/types";

type ComponentTemplate =
{
    component: VueComponent,
    props: Dictionary<string, any>,
};

export type ComponentsBySlots = Dictionary<string, ComponentTemplate[]>;

/**
 * All in one method: create components from state, do security check, and render them
 */
export const getContentElements = (state: State<{}, {}, {}, {}>, parts: { [key: string]: VueComponent }, additionalProps?: any): (JSX.Element | JSX.Element[]) =>
{
    const componentsBySlots = getComponentsBySlots(state, parts);
    return renderErrorMessage(state) ?? renderComponentsBySlots(state, componentsBySlots, additionalProps);
}

/**
 * Returns an error banner if the state does not have security. Returns an error body if a Chart Tile contains an error. Otherwise, returns null.
 */
export const renderErrorMessage = (state: State<{}, {}, {}, {}>): (JSX.Element | null) =>
{
    if (state.errorState === ErrorStates.OK)
    {
        if ((state.features.dataSource.typeIdentifier == "chartTileDataSource" || state.features.dataSource.typeIdentifier == "previewChartTileDataSource") && state.data?.nonPageableValues?.error)
        {
            const dashboardItemStateFeatures = state.parent!.features as unknown as DashboardItemFeatures;

            return <TileErrorContent error={state.data?.nonPageableValues?.error}
                width={dashboardItemStateFeatures.settings.width}
                height={dashboardItemStateFeatures.settings.height}
                iconColor={dashboardItemStateFeatures.settings.parameters.iconColor as string}
                backgroundColor={dashboardItemStateFeatures.settings.parameters.backgroundColor as string} />;
        }

        return null;
    }
    else if (state.features.securityData.securityMessage)
    {
        return <Cma
            onClick={(event) => { event.stopPropagation(); }}
            message={state.features.securityData.securityMessage}
        />;
    }
    else
    {
        return <></>;
    }
};

/**
 * Renders the component dictionary produced by @see getComponentsBySlots .
 */
export const renderComponentsBySlots = (state: State<{}, {}, {}, {}>, componentsBySlots: ComponentsBySlots, additionalProps?: any) =>
{
    const slots: Dictionary<string, (callerProps?: any, callerSlots?: any) => (VNode | null)[]> = {};
    for (const [key, components] of Object.entries(componentsBySlots))
    {
        slots[key] = (callerProps?: any, callerSlots?: any) => components!.map(({ component, props }) =>
        {
            const value = h(
                component,
                Object.assign({ parent: state }, additionalProps, props, callerProps, state.features.props),
                callerSlots
            );
            return value;
        });
    }

    const template = componentsBySlots.template![0];
    return h(template.component, Object.assign({}, additionalProps, template.props, state.features.template, state.features.props), slots);
};

/**
 * Convert state.features.components into a dictionary of Vue components that can be rendered.
 */
export const getComponentsBySlots = (state: State<{}, {}, {}, {}>, parts: { [key: string]: VueComponent }): ComponentsBySlots =>
{
    const componentsBySlots: ComponentsBySlots = {};

    for (const component of state.features.components)
    {
        if (component === undefined || component == null)
        {
            continue;
        }

        addComponent(component, parts, componentsBySlots);
    }

    const templateComponent = parts[state.features.template.typeIdentifier];
    componentsBySlots.template = [{ component: templateComponent, props: {} }];

    return componentsBySlots;
}

export const getHtmlDataAttributes = (data: Dictionary<string, string> | undefined) =>
{
    return Object.fromEntries(Object.entries(data ?? {}).map(([k, v]) => [`data-${k}`, v]));
};

/**
 * Get a single component from a state.features.components[].type key.
 */
const getComponent = (key: string, parts: { [key: string]: VueComponent }): VueComponent =>
{
    const comp = parts[key] ?? SkyVueTypes[key.toLowerCase()];
    if (comp === undefined)
    {
        throw new SkywardException(`No skyVue component named ${key}`);
    }

    return comp;
}

/**
 * Append a single state.features.components entry to a ComponentsBySlots dictionary.
 */
const addComponent = (component: SkyVueComponent, parts: { [key: string]: VueComponent }, componentsBySlots: ComponentsBySlots): void =>
{
    const slot = (component.slot as string) ?? "default";
    const comp = getComponent(component.type, parts);
    let props = component;
    if (isSkyVueComponent(component))
    {
        props = {
            ...props,
            ...getHtmlDataAttributes(component.skyVueData?.htmlData),
            class: component.skyVueData?.classNames,
        };
    }

    // Append the new component
    componentsBySlots[slot] ??= [];
    componentsBySlots[slot]!.push({ component: comp, props });
}

/**
 * Return a promise that resolves with the given ref's value when it changes from null/undefined to a value.
 */
export function refValueIsSet<T>(ref: Ref<T> | (() => T)): Promise<NonNullable<T>>;
export function refValueIsSet<T>(ref: Ref<T> | (() => T), condition: (val: T) => boolean): Promise<T>;
export function refValueIsSet<T>(ref: Ref<T> | (() => T), condition?: (val: T) => boolean): Promise<T>
{
    const getter = typeof ref === "function" ? ref : () => ref.value;

    let unwatch: WatchStopHandle;
    return new Promise<T>((res, rej) =>
    {
        unwatch = watchEffect(() =>
        {
            try
            {
                const value = getter();
                const success = typeof condition === "function" ? condition(value) : Boolean(value);
                if (success)
                {
                    res(value);
                }
            }
            catch (e)
            {
                rej(e);
            }
        });
    })
    .finally(() =>
    {
        unwatch?.();
    });
};

export function managedListener<TType extends string>(events: TType, handler: JQuery.TypeEventHandler<HTMLElement, undefined, HTMLElement, HTMLElement, TType>) 
{
    $(document.body).on(events, handler);
    onScopeDispose(() => $(document.body).off(events, handler));
}